import $http from '@/plugins/axios'
import { removeNamespace } from '@/utils/helpers'
import { clearItem, storeItem } from '@/utils/local-storage'
import { acquireTokenSilent, azureADSignIn, azureADSignOut } from '@/utils/msal'
import router from '../../router'
import LogRocket from 'logrocket'

export const AUTH_TYPES = {
  getters: {
    isAuthenticated: 'auth/isAuthenticated',
    hasPermission: 'auth/hasPermission',
    currentTenant: 'auth/currentTenant',
    userTenants: 'auth/userTenants'
  },
  mutations: {
    SET_AUTH_USER: 'auth/SET_AUTH_USER',
    SET_PERMISSIONS: 'auth/SET_PERMISSIONS',
    SET_ACCESS_TOKEN: 'auth/SET_ACCESS_TOKEN',
    SET_CURRENT_TENANT_ID: 'auth/SET_CURRENT_TENANT_ID',
    SET_USER_TENANTS: 'auth/SET_USER_TENANTS'
  },
  actions: {
    clearSession: 'auth/clearSession',
    b2cSignIn: 'auth/b2cSignIn',
    b2cSignOut: 'auth/b2cSignOut',
    trySetAccessToken: 'auth/trySetAccessToken',
    fetchAuthUser: 'auth/fetchAuthUser',
    switchTenant: 'auth/switchTenant'
  }
}

const _types = removeNamespace('auth', AUTH_TYPES)

const DEFAULT_TENANT_ID = 1

const state = () => ({
  authenticatedUser: false,
  userPermissions: [],
  accessToken: null,
  currentTenantId: localStorage.getItem('currentTenantId') || null,
  userTenants: []
})

const getters = {
  [_types.getters.isAuthenticated] (state) {
    return !!state.accessToken
  },
  [_types.getters.hasPermission]: state => (permission) => {
    return !!state.userPermissions.find(p => p.name === permission)
  },
  [_types.getters.currentTenant] (state) {
    if (!state.userTenants) return { }

    return state.userTenants?.find(x => x.id === parseInt(state.currentTenantId))
  },
  [_types.getters.userTenants] (state) {
    return state.userTenants
  }
}

const mutations = {
  [_types.mutations.SET_AUTH_USER] (state, user) {
    state.authenticatedUser = user
  },
  [_types.mutations.SET_PERMISSIONS] (state, permissions) {
    state.userPermissions = permissions
  },
  [_types.mutations.SET_ACCESS_TOKEN] (state, token) {
    state.accessToken = token
  },
  [_types.mutations.SET_CURRENT_TENANT_ID] (state, tenantId) {
    localStorage.removeItem("currentTenantId")
    localStorage.setItem("currentTenantId", JSON.stringify(tenantId))
    state.currentTenantId = tenantId
  },
  [_types.mutations.SET_USER_TENANTS] (state, userTenants) {
    state.userTenants = userTenants
  }
}

const actions = {
  [_types.actions.clearSession] ({ dispatch }) {
    clearItem('refresh_valid_till')
    clearItem('user_data')
    this._vm.$appInsights.clearAuthenticatedUserContext()
    return dispatch(_types.actions.b2cSignOut)
  },
  [_types.actions.b2cSignIn] () {
    azureADSignIn(null)
  },
  [_types.actions.b2cSignOut] () {
    azureADSignOut()
  },
  [_types.actions.trySetAccessToken] ({ commit, dispatch }, fetchUser = true) {
    return acquireTokenSilent()
      .then((authResponse) => {
        // set token
        commit(_types.mutations.SET_ACCESS_TOKEN, authResponse.accessToken || '')
        storeItem('access_token', authResponse.accessToken)
        // set refresh lifetime
        const validUntil = Date.now() + (14 * 24 * 60 * 60 * 1000) // 14 day refresh token lifetime
        storeItem('refresh_valid_till', validUntil)
        // fetch user
        return (fetchUser)
          ? dispatch(_types.actions.fetchAuthUser, authResponse.accessToken)
          : new Promise(resolve => resolve(true))
      })
      .catch(() => {}) // Errors will be handled in msal callback, so just ignore here
  },
  [_types.actions.fetchAuthUser] ({ commit, state }, token = null) {
    const api = (token) ? $http(token) : $http()
    return api.get('store/users/current-user')
      .then((res) => {
        const user = res
        commit(_types.mutations.SET_AUTH_USER, user)

        const userTenants = []
        user.userRoles.forEach((role) => {
          if (userTenants.findIndex(x => x.id === role.tenantId) === -1) {
            userTenants.push(role.tenant)
          }
        })

        // if the current userTenantId is null, set it to the first one in the list
        if (!state.currentTenantId || userTenants.findIndex(x => x.id === parseInt(state.currentTenantId)) === -1) {
          const tenantId = user.userRoles[0]?.tenantId
          commit(_types.mutations.SET_CURRENT_TENANT_ID, tenantId || DEFAULT_TENANT_ID)
        }

        commit(_types.mutations.SET_USER_TENANTS, userTenants)

        if (user && user.email) {
          this._vm.$appInsights.setAuthenticatedUserContext(user.email)
          if (process.env.VUE_APP_LOGROCKET_KEY) {
            LogRocket.identify(user.email, {
              name: user.userProfile ? `${user.userProfile.firstName} ${user.userProfile.lastName}` : user.email,
              email: user.email
            })
          }
        }
        storeItem('user_data', user, true)
        const permissions = []
        user.userRoles.forEach(r => r.role.rolePermissions.forEach(rp => {
          if (!permissions.find(val => val.id === rp.permission.id)) { permissions.push(rp.permission) }
        }))
        commit(_types.mutations.SET_PERMISSIONS, permissions)
        if (router.currentRoute.name === 'Unauthorized') {
          router.push('/')
        }
      })
  },
  [_types.actions.switchTenant] ({ commit }, id = DEFAULT_TENANT_ID) {
    commit(_types.mutations.SET_CURRENT_TENANT_ID, id)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
